import { EventEmitter, Injectable } from "@angular/core";
import { AuthUser } from "@app/auth/model/auth-user";
import { TemporaryProjectFilter } from "@app/project/model";
import { ProjectView } from "@app/project/model/project-view";
import { ProjectSelectors } from "@app/project/store";
import { LoadingState } from "@app/root-store/api-result.state";
import { SessionStorageService } from "@app/shared/services/storage/session-storage.service";
import { Store, select } from "@ngrx/store";
import { filter, map, tap } from "rxjs/operators";
import { ProjectViewActions } from "../actions";
import { ProjectState } from "../projects.state";

@Injectable({
    providedIn: "root",
})
export class ProjectViewDispatchers {
    static LOCAL_PROJECT_VIEW_KEY = "projectView";
    getProjectViews$(entityId: number, userId: number) {
        return this.store.pipe(
            select(ProjectSelectors.getProjectViewsState),
            tap(projectViewState => {
                if (projectViewState?.callState === LoadingState.init) {
                    this.store.dispatch(ProjectViewActions.load({ entityId, userId }));
                }
            }),
            filter(projectViewState => projectViewState?.callState === LoadingState.loaded),
            map(projectViewState => Object.values(projectViewState.result || {}))
        );
    }

    getSimplifiedColumns(authUser: AuthUser) {
        return this.store.pipe(select(ProjectSelectors.getSimplifiedColumns(authUser)));
    }

    getProjectViewsArray() {
        return this.store.pipe(select(ProjectSelectors.getProjectViewsArray));
    }

    getActiveProjectView$() {
        return this.store.pipe(select(ProjectSelectors.getActiveProjectView));
    }

    updateProjectView(projectView: ProjectView, callbackEmitter?: EventEmitter<any>) {
        this.store.dispatch(ProjectViewActions.update({ projectView, callbackEmitter }));
    }

    createProjectView(projectView: ProjectView, callbackEmitter?: EventEmitter<any>) {
        this.store.dispatch(ProjectViewActions.create({ projectView, callbackEmitter }));
    }

    upsertProjectView(projectView: ProjectView, callbackEmitter?: EventEmitter<any>) {
        if (projectView.id) {
            this.updateProjectView(projectView, callbackEmitter);
        } else {
            this.createProjectView(projectView, callbackEmitter);
        }
    }

    deleteProjectView(projectView: ProjectView, callbackEmitter?: EventEmitter<any>, authUser?: AuthUser) {
        this.store.dispatch(ProjectViewActions.deleteConfirmation({ projectView, callbackEmitter, authUser }));
    }

    dispatchUpdateProjectViewSuccessAction(projectView: ProjectView) {
        this.store.dispatch(ProjectViewActions.updateSuccess({ projectView }));
        this.persistViewLocal({ view: projectView.id || undefined });
    }

    persistViewLocal(viewObject: Partial<TemporaryProjectFilter>) {
        const saved = this.sessionService.getObjectItem(ProjectViewDispatchers.LOCAL_PROJECT_VIEW_KEY) || {};
        const newObject = {
            ...saved,
            ...viewObject,
        };
        this.sessionService.saveItem(ProjectViewDispatchers.LOCAL_PROJECT_VIEW_KEY, newObject);
    }

    getCurrentProjectFilter(): Partial<TemporaryProjectFilter> {
        return this.sessionService.getObjectItem(ProjectViewDispatchers.LOCAL_PROJECT_VIEW_KEY) || {};
    }

    dispatch(action: any) {
        this.store.dispatch(action);
    }

    constructor(private readonly store: Store<ProjectState>, private readonly sessionService: SessionStorageService) {}
}
